<template>
  <fieldset class="card">
    <div class="card-header">
      General Information
    </div>
    <div class="card-body">
      <div class="form-group row">
        <div class="col-md-5 text-md-right">
          <i
            v-tooltip:top="'The information you provide is anonymous and will only be used to help better serve you. Birth sex is an important, structured variable that is used to facilitate efficient, equitable and patient-centered care. Birth sex in an EHR is often used to cue the automatic, timely generation of health screens (ie. pap smears, prostate exams), and calculate medication dosages and laboratory test ranges by adjusting for hormonal history and anatomy.'"
            class="fa fa-info-circle text-primary d-inline"
          />
          <strong>Sex at Birth:</strong>
        </div>
        <div class="col-md-7">
          <div class="form-check form-check-inline">
            <input
              id="assessment-general-gender-m"
              v-model="healthData.latest.answers.general.gender"
              class="form-check-input"
              name="gender"
              type="radio"
              value="m"
            >
            <label
              class="form-check-label"
              for="assessment-general-gender-m"
            >
              Male
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              id="assessment-general-gender-f"
              v-model="healthData.latest.answers.general.gender"
              class="form-check-input"
              name="gender"
              type="radio"
              value="f"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-gender-f"
            >
              Female
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="!shortAssessment"
        class="form-group row"
      >
        <div class="col-md-5 text-md-right">
          <label for="assessment-general-birthdate">
            <strong>Your Birth Date:</strong>
          </label>
        </div>
        <div class="col-md-5 col-lg-5">
          <div class="input-group">
            <datepicker
              v-model="birthdate"
              input-class="form-control"
              placeholder="MM/DD/YYYY"
              typeable
              :format="dateFormatter"
            />
            <div class="input-group-append">
              <div class="input-group-text">
                <i class="fa fa-calendar" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!shortAssessment"
        class="form-group row"
      >
        <div class="col-md-5 text-md-right">
          <label>
            <strong>Ethnicity:</strong>
          </label>
        </div>
        <div class="col-md-7">
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-caucasian"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="caucasian"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-caucasian"
            >
              Caucasian
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-african-american"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="african-american"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-african-american"
            >
              African American
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-asian"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="asian"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-asian"
            >
              Asian
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-hispanic"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="hispanic"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-hispanic"
            >
              Hispanic
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-indian"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="indian"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-indian"
            >
              Indian
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-native-american"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="native-american"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-native-american"
            >
              Native American
            </label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-ethnicity-other"
              v-model="healthData.latest.answers.general.ethnicity"
              class="form-check-input"
              name="ethnicity"
              type="radio"
              value="other"
              required
            >
            <label
              class="form-check-label"
              for="assessment-general-ethnicity-other"
            >
              Other
            </label>
          </div>
        </div>
      </div>

      <div class="form-group row">
        <div class="col-md-5 text-md-right">
          <label for="assessment-general-measurements-height-feet">
            <strong>Height:</strong>
          </label>
        </div>
        <div class="col-6 col-md-3 col-lg-2">
          <div class="input-group">
            <input
              id="assessment-general-measurements-height-feet"
              v-model="healthData.latest.answers.general.measurements.height.feet"
              type="number"
              class="form-control"
              step="1"
              min="0"
              max="10"
              required
            >
            <div class="input-group-append">
              <div class="input-group-text">
                ft.
              </div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3 col-lg-2">
          <div class="input-group">
            <input
              id="assessment-general-measurements-height-inches"
              v-model="healthData.latest.answers.general.measurements.height.inches"
              type="number"
              class="form-control"
              step="1"
              min="0"
              max="12"
              required
            >
            <div class="input-group-append">
              <div class="input-group-text">
                in.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group row">
        <div class="col-md-5 text-md-right">
          <label for="assessment-general-measurements-weight">
            <strong>Weight:</strong>
          </label>
        </div>
        <div class="col-md-3 col-lg-2">
          <div class="input-group">
            <input
              id="assessment-general-measurements-weight"
              v-model="healthData.latest.answers.general.measurements.weight"
              type="number"
              step="0.1"
              min="0"
              max="800"
              class="form-control"
              required
            >
            <div class="input-group-append">
              <div class="input-group-text">
                lbs.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!shortAssessment">
        <div class="form-group row">
          <div class="col-md-5 text-md-right">
            <modal
              button_text="View Chart"
              :title="'Measurement Chart'"
              :button_classes="'text-primary'"
            >
              <img
                src="/img/health-info/hip_waist_chart.png"
                style="object-fit: scale-down;"
                alt="Measurement Chart"
                class="measurement-chart"
              >
            </modal>
            <i
              v-tooltip:top="'Waist: Measure at its narrowest point width-wise, usually just above the navel.'"
              class="fa fa-info-circle text-primary d-inline"
            />
            <label
              class="d-inline"
              for="assessment-general-measurements-waist"
            >
              <strong>Waist Measurement:</strong>
            </label>
          </div>
          <div class="col-md-3 col-lg-2">
            <div class="input-group">
              <input
                id="assessment-general-measurements-waist"
                v-model="healthData.latest.answers.general.measurements.waist"
                type="number"
                step="0.1"
                min="0"
                max="100"
                class="form-control"
                required
              >
              <div class="input-group-append">
                <div class="input-group-text">
                  in.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row">
          <div class="col-md-5 text-md-right">
            <modal
              button_text="View Chart"
              :title="'Measurement Chart'"
              :button_classes="'text-primary'"
            >
              <img
                src="/img/health-info/hip_waist_chart.png"
                style="object-fit: scale-down;"
                alt="Measurement Chart"
              >
            </modal>
            <i
              v-tooltip:top="'Hips: Measure around the widest part of the hipbones.'"
              class="fa fa-info-circle text-primary d-inline"
            />
            <label
              class="d-inline"
              for="assessment-general-measurements-hips"
            >
              <strong>Hip Measurement:</strong>
              <br>
              (optional - leave blank if you do not know)
            </label>
          </div>
          <div class="col-md-3 col-lg-2">
            <div class="input-group">
              <input
                id="assessment-general-measurements-hips"
                v-model="healthData.latest.answers.general.measurements.hips"
                type="number"
                step="0.1"
                min="0"
                max="100"
                class="form-control"
              >
              <div class="input-group-append">
                <div class="input-group-text">
                  in.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row">
          <div class="col-md-5 text-md-right">
            <label for="assessment-general-measurements-bodyfat">
              <strong>Percent Body Fat:</strong>
              <br>
              (optional - leave blank if you do not know)
            </label>
          </div>
          <div class="col-md-3 col-lg-2">
            <div class="input-group">
              <input
                id="assessment-general-measurements-bodyfat"
                v-model="healthData.latest.answers.general.measurements.bodyfat"
                type="number"
                step="1"
                min="0"
                max="100"
                class="form-control"
              >
              <div class="input-group-append">
                <div class="input-group-text">
                  %
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group row">
          <div class="col-md-5 text-md-right">
            <i
              v-tooltip:top="'Place your index and third fingers on your neck to the side of your windpipe. To check your pulse at your wrist, place two fingers between the bone and the tendon over your radial artery — which is located on the thumb side of your wrist. When you feel your pulse, count the number of beats in 15 seconds. Multiply this number by four to calculate your beats per minute.'"
              class="fa fa-info-circle text-primary d-inline"
            />
            <label
              class="d-inline"
              for="assessment-general-measurements-pulse"
            >
              <strong>Pulse:</strong>
              <br>
              (optional)
            </label>
          </div>
          <div class="col-md-3 col-lg-2">
            <div class="input-group">
              <input
                id="assessment-general-measurements-pulse"
                v-model="healthData.latest.answers.general.measurements.pulse"
                type="number"
                step="1"
                min="0"
                class="form-control"
              >
              <div class="input-group-append">
                <div class="input-group-text">
                  bpm
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group row">
        <div class="col-md-5 text-md-right">
          <label>
            <strong>What is the main reason that you chose to be a part of the Food Fitness First, Inc.&reg; program?</strong>
          </label>
        </div>
        <div class="col-md-7">
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-healthier"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="healthier"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-healthier"
              class="form-check-label"
            >I want to learn how to make healthier choices, using the Healthy FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-healthier"
              class="form-check-label"
            >I want to learn how to make Healthier choices.</label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-sodium"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="sodium"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-sodium"
              class="form-check-label"
            >I want to decrease my sodium intake, using the Low Sodium FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-sodium"
              class="form-check-label"
            >I want to decrease my Sodium intake.</label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-diabetes"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="diabetes"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-diabetes"
              class="form-check-label"
            >I want to improve my diabetes, lose weight, eat more fiber, or prevent pre-diabetes, using the Diabetes FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-diabetes"
              class="form-check-label"
            >I want to improve my Diabetes, Lose Weight, or Prevent Pre-Diabetes.</label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-carb"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="carb"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-carb"
              class="form-check-label"
            >I want to decrease my carbohydrate intake (Count Carbs), using the Carb Counting FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-carb"
              class="form-check-label"
            >I want to decrease my Carbohydrate Intake. (Count Carbs)</label>
          </div>
          <div
            v-show="!shortAssessment"
            class="form-check"
          >
            <input
              id="assessment-general-why-fff-reason-fiber"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="fiber"
              required
            >
            <label
              for="assessment-general-why-fff-reason-fiber"
              class="form-check-label"
            >I want to increase my Fiber intake.</label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-renal-nutrition"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="renal-nutrition"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-renal-nutrition"
              class="form-check-label"
            >I want to learn more about renal nutrition, using the Renal FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-renal-nutrition"
              class="form-check-label"
            >I want to learn more about Renal nutrition.</label>
          </div>
          <div class="form-check">
            <input
              id="assessment-general-why-fff-reason-renal-diabetes-nutrition"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="renal-diabetes-nutrition"
              required
            >
            <label
              v-if="shortAssessment"
              for="assessment-general-why-fff-reason-renal-diabetes-nutrition"
              class="form-check-label"
            >I want to learn more about both renal disease and diabetes nutrition, using the Renal Diabetes FOOD SPIRAL®.</label>
            <label
              v-else
              for="assessment-general-why-fff-reason-renal-diabetes-nutrition"
              class="form-check-label"
            >I want to learn more about both Renal Disease and Diabetes nutrition.</label>
          </div>
          <div
            v-show="!shortAssessment"
            class="form-check"
          >
            <input
              id="assessment-general-why-fff-reason-other"
              v-model="healthData.latest.answers.general.why_fff.reason"
              class="form-check-input"
              name="assessment-general-why-fff-reason"
              type="radio"
              value="other"
              required
            >
            <label
              for="assessment-general-why-fff-reason-other"
              class="form-check-label"
            >Other reason(s) <small> - Describe below</small></label>
          </div>
        </div>
      </div>

      <div
        v-show="healthData.latest.answers.general.why_fff.reason == 'other'"
        class="form-group row"
      >
        <div class="col-md-5 text-md-right">
          <label for="assessment-general-why-fff-reason-other-desc">
            <strong>Please describe any other reasons you joined the Food Fitness First® program:</strong>
            <br>
            (optional)
          </label>
        </div>
        <div class="col-md-7">
          <textarea
            id="assessment-general-why-fff-reason-other-desc"
            v-model="healthData.latest.answers.general.why_fff.reason_desc"
            placeholder="Describe your reasons"
            class="form-control"
          />
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';
import {mapState} from 'vuex';
import Modal from '../Modal.vue';

export default {
  components: {
    Datepicker,
    Modal,
  },
  props: {
    shortAssessment: Boolean,
  },
  computed: {
    birthdate: {
      get: function() {
        // Get date from store
        let date = this.$store.state.healthData.latest.answers.general.birthdate;
        // If valid or initialized, convert to a Date object; If not, return a Date object set to now
        if (date) {
          return moment(this.$store.state.healthData.latest.answers.general.birthdate, 'MM/DD/YYYY').toDate();
        } else {
          return Date();
        }
      },
      set: function(date) {
        if (!moment(date).isValid()) {
          this.$store.state.healthData.latest.answers.general.birthdate = '';
          return;
        }
        // Convert date object to a string format for sending to API
        this.$store.state.healthData.latest.answers.general.birthdate = moment(date).format('MM/DD/YYYY');
      }
    },
    ...mapState([
      'healthData'
    ])
  },
  methods: {
    dateFormatter(date){
      return moment(date).format('MM/DD/YYYY');
    },
  }

}
</script>
<style lang="scss">
    .measurement-chart {
        $phone: "only screen and (min-width : 200px)";
        $ipad: "only screen and (min-width : 500px)";
        $medium: "only screen and (min-width : 800px)";
        $large: "only screen and (min-width : 1000px)";

        @media #{$phone} {
            width: 300px;
        }

        @media #{$ipad} {
            width: 400px;
        }

        @media #{$medium} {
            width: 600px;
        }

        @media #{$large} {
            width: 700px;
        }

    }


</style>
